import React, {useState} from "react";
import {useDispatch} from "react-redux";
import {DeleteDialogProps} from "@components/DeleteDialog";
import {EditDialogProps} from "@components/EditDialog";
import {api} from "@core/api";
import {UseAddEntity, UseDeleteEntity, UseUpdateEntity} from "@core/api/crud";
import {BaseEntity, BaseEntityForm, entity} from "@core/model";
import {SvgIconOwnProps} from "@mui/material";
import {FormLabel} from "@components";

export type DialogAction = "add" | "edit" | "delete";

export interface EntityDialogConfig<Entity extends BaseEntity> {
  entity: entity;
  EntityIcon: React.ComponentType<SvgIconOwnProps>;
  form: BaseEntityForm;
  selectValues?: Record<string, Array<{id: string; name: string}>>;
  label?: Partial<FormLabel>;
  invalidateTags: {type: entity; id: string}[];
  updateCallback?: (data: Partial<Entity>, action: DialogAction) => void;
  useAddEntity: UseAddEntity<Entity>;
  useUpdateEntity: UseUpdateEntity<Entity>;
  useDeleteEntity: UseDeleteEntity;
}

export type CrudDialogProps<Entity extends BaseEntity> = (
  | DeleteDialogProps<Entity>
  | EditDialogProps<Entity>
) & {
  action: DialogAction | undefined;
};

export interface UseEntityDialogReturn<Entity extends BaseEntity> {
  dialog: CrudDialogProps<Entity>;
  isLoading: boolean;
  setDialog: React.Dispatch<
    React.SetStateAction<
      (DeleteDialogProps<Entity> | EditDialogProps<Entity>) & {
        action: DialogAction | undefined;
      }
    >
  >;
  handleEditClick: (
    action: DialogAction,
    data: Partial<Entity>,
    form?: BaseEntityForm,
  ) => void;
  closeDialog: () => void;
}

export const useEntityDialog = <Entity extends BaseEntity>(
  config: EntityDialogConfig<Entity>,
): UseEntityDialogReturn<Entity> => {
  const dispatch = useDispatch();
  const [add, {isLoading: isAddLoading}] = config.useAddEntity<Entity>();
  const [update, {isLoading: isUpdateLoading}] =
    config.useUpdateEntity<Entity>();
  const [deleteByIds, {isLoading: isDeleteLoading}] =
    config.useDeleteEntity<Entity>();

  const isEditLoading = isAddLoading || isUpdateLoading || isDeleteLoading;

  const defaultLabel = {
    title: "deleteDialog.title",
    text: "deleteDialog.text",
  };

  const closeDialog = () => {
    setDialog(prev => ({...prev, open: false}));
    setTimeout(() => {
      setDialog(prev => ({
        ...prev,
        form: config.form,
        data: {},
        action: undefined,
      }));
    }, 300);
  };

  const [dialog, setDialog] = useState<
    (DeleteDialogProps<Entity> | EditDialogProps<Entity>) & {
      action: DialogAction | undefined;
    }
  >({
    open: false,
    form: config.form,
    data: {} as Partial<Entity>,
    isLoading: false,
    action: undefined,
    entity: config.entity,
    EntityIcon: config.EntityIcon,
    selectValues: config.selectValues || {},
    label: {...defaultLabel, ...config.label},
    onClose: closeDialog,
    onConfirm: () => {},
  });

  const handleConfirm = async (
    action: DialogAction,
    entity: Partial<Entity>,
  ): Promise<void> => {
    try {
      const callback = (data: Partial<Entity>, action: DialogAction) => {
        if (config.updateCallback) {
          config.updateCallback(data, action);
        }
        dispatch(
          api.util.invalidateTags([
            ...config.invalidateTags,
            {
              type: config.entity,
              id: entity.id,
            },
          ]),
        );
        closeDialog();
      };

      setDialog(prev => ({...prev, isLoading: true}));

      if (action === "delete" && entity.id) {
        deleteByIds([entity.id])
          .unwrap()
          .then(() => callback(entity, action));
      } else if (action === "edit" && entity.id) {
        update(entity)
          .unwrap()
          .then(() => callback(entity, action));
      } else {
        add(entity)
          .unwrap()
          .then(r => callback(r.data, action));
      }
    } catch (error) {
      console.error(`Error in ${action} operation:`, error);
    }
  };

  const handleEditClick = (
    action: DialogAction,
    data: Partial<Entity>,
    form?: BaseEntityForm,
  ) => {
    setDialog(prev => ({
      ...prev,
      isLoading: false,
      open: true,
      action,
      data,
      form: form ?? (prev as EditDialogProps<Entity>).form,
      onConfirm: (e: Partial<Entity>) => handleConfirm(action, e),
    }));
  };

  return {
    dialog,
    isLoading: isEditLoading,
    setDialog,
    closeDialog,
    handleEditClick,
  };
};
